import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { alert, AlertTypes } from '~types/AlertTypes';

const settingSchema = yup.object().shape({
  defaultPickupOnsiteMinutes: yup
    .number()
    .required(t('form_validation_errors.required_simple'))
    .typeError(t('form_validation_errors.should_be_number_simple'))
    .min(0, t('form_validation_errors.minimum_is_num', { num: 0 })),
  defaultDropoffOnsiteMinutes: yup
    .number()
    .required(t('form_validation_errors.required_simple'))
    .typeError(t('form_validation_errors.should_be_number_simple'))
    .min(0, t('form_validation_errors.minimum_is_num', { num: 0 })),
});

type SettingDTO = yup.InferType<typeof settingSchema>;

function OnsiteDefaultsSetting() {
  const { userStore, toasterStore } = useStores();
  const { updateCompany } = useCompany();
  const [isSaving, setIsSaving] = useState(false);
  const [inSaveMode, setInSaveMode] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SettingDTO>({
    resolver: yupResolver(settingSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      defaultPickupOnsiteMinutes: userStore.userCompany.defaultPickupOnsiteMinutes ?? 0,
      defaultDropoffOnsiteMinutes: userStore.userCompany.defaultDropoffOnsiteMinutes ?? 0,
    },
  });

  const saveOnsiteDefaults = (data: SettingDTO) => {
    setIsSaving(true);
    updateCompany({
      company: {
        id: userStore.userCompany.id,
        defaultPickupOnsiteMinutes: data.defaultPickupOnsiteMinutes,
        defaultDropoffOnsiteMinutes: data.defaultDropoffOnsiteMinutes,
      },
    })
      .then(() => {
        setInSaveMode(false);
        toasterStore.push(
          alert(
            t('administration.configuration.configuration_saved'),
            AlertTypes.success,
          ),
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <Typography sx={{ mb: 1 }}>
        {t('administration.configuration.settings.onsite_defaults.setting_subtitle')}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ sm: 3 }}>
          <TextFormField
            control={control}
            errors={errors}
            disabled={!inSaveMode}
            name="defaultPickupOnsiteMinutes"
            type="number"
            label={`${t('administration.configuration.settings.onsite_defaults.default_pickup_onsite_minutes')}`}
          />
        </Grid>

        <Grid size={{ sm: 3 }}>
          <TextFormField
            control={control}
            errors={errors}
            disabled={!inSaveMode}
            name="defaultDropoffOnsiteMinutes"
            type="number"
            label={`${t('administration.configuration.settings.onsite_defaults.default_dropoff_onsite_minutes')}`}
          />
        </Grid>

        <Grid sx={{ alignSelf: 'flex-end', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {inSaveMode && (
              <SmallButton
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setInSaveMode(false);
                  reset({
                    defaultPickupOnsiteMinutes:
                      userStore.userCompany.defaultPickupOnsiteMinutes ?? 0,
                    defaultDropoffOnsiteMinutes:
                      userStore.userCompany.defaultDropoffOnsiteMinutes ?? 0,
                  });
                }}
                startIcon={<Close />}
              >
                {t('actions.cancel')}
              </SmallButton>
            )}
            <SmallLoadingButton
              loading={isSaving}
              variant="contained"
              color="brandV2Yellow"
              startIcon={inSaveMode ? <Check /> : null}
              onClick={
                inSaveMode ? handleSubmit(saveOnsiteDefaults) : () => setInSaveMode(true)
              }
            >
              {inSaveMode ? t('actions.save') : t('actions.edit')}
            </SmallLoadingButton>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default {
  component: OnsiteDefaultsSetting,
  canAccess: (userStore: UserStore) => {
    return userStore.getPermissions().canEditCompany;
  },
};
